import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { tableLabel } from "@/lib/tables";
import AppHeader from "@/components/AppHeader";
import { ArrowLeft, Plus, Minus, Trash2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function WaiterOrderPanel() {
    const { tableNumber } = useParams();
    const tableNum = parseInt(tableNumber, 10);
    const navigate = useNavigate();
    const location = useLocation();
    const isKitchenFlow = location.pathname.startsWith("/mutfak");
    const backPath = isKitchenFlow ? "/mutfak" : "/garson";
    const backLabel = isKitchenFlow ? "Mutfağa dön" : "Masalara dön";

    const [menu, setMenu] = useState([]);
    const [order, setOrder] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [cart, setCart] = useState({}); // {menu_item_id: {item, qty, notes}}
    const [sending, setSending] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [m, o] = await Promise.all([
                api.get("/menu"),
                api.get(`/orders/table/${tableNum}`),
            ]);
            setMenu(m.data.filter((x) => x.available));
            setOrder(o.data || null);
            if (m.data.length && activeCategory === null) {
                setActiveCategory(m.data[0].category);
            }
        } catch (e) {
            toast.error(formatError(e));
        }
    }, [tableNum, activeCategory]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const categories = useMemo(
        () => Array.from(new Set(menu.map((m) => m.category))),
        [menu]
    );
    const filteredMenu = useMemo(
        () => menu.filter((m) => m.category === activeCategory),
        [menu, activeCategory]
    );

    const addToCart = (item) => {
        setCart((prev) => {
            const existing = prev[item.id];
            return {
                ...prev,
                [item.id]: {
                    item,
                    qty: existing ? existing.qty + 1 : 1,
                    notes: existing?.notes || "",
                },
            };
        });
    };

    const updateQty = (id, delta) => {
        setCart((prev) => {
            const existing = prev[id];
            if (!existing) return prev;
            const newQty = existing.qty + delta;
            if (newQty <= 0) {
                const next = { ...prev };
                delete next[id];
                return next;
            }
            return { ...prev, [id]: { ...existing, qty: newQty } };
        });
    };

    const setQty = (id, value) => {
        setCart((prev) => {
            const existing = prev[id];
            if (!existing) return prev;
            // Allow empty string while user is typing; treat as 1 on blur
            if (value === "") {
                return { ...prev, [id]: { ...existing, qty: "" } };
            }
            const n = parseInt(value, 10);
            if (Number.isNaN(n) || n < 0) return prev;
            if (n === 0) {
                const next = { ...prev };
                delete next[id];
                return next;
            }
            const clamped = Math.min(n, 99);
            return { ...prev, [id]: { ...existing, qty: clamped } };
        });
    };

    const normalizeQty = (id) => {
        setCart((prev) => {
            const existing = prev[id];
            if (!existing) return prev;
            if (existing.qty === "" || existing.qty < 1) {
                return { ...prev, [id]: { ...existing, qty: 1 } };
            }
            return prev;
        });
    };

    const updateNotes = (id, notes) => {
        setCart((prev) => {
            if (!prev[id]) return prev;
            return { ...prev, [id]: { ...prev[id], notes } };
        });
    };

    const removeFromCart = (id) => {
        setCart((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const cartTotal = Object.values(cart).reduce((s, c) => s + c.item.price * (parseInt(c.qty, 10) || 0), 0);
    const cartItems = Object.values(cart);

    const sendToKitchen = async () => {
        if (!cartItems.length) return;
        setSending(true);
        try {
            const payload = {
                table_number: tableNum,
                items: cartItems.map((c) => ({
                    menu_item_id: c.item.id,
                    qty: Math.max(1, parseInt(c.qty, 10) || 1),
                    notes: c.notes || "",
                })),
            };
            await api.post("/orders", payload);
            toast.success("Sipariş mutfağa gönderildi");
            setCart({});
            if (isKitchenFlow) {
                navigate("/mutfak");
            } else {
                loadAll();
            }
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSending(false);
        }
    };

    const removeExistingItem = async (itemId) => {
        if (!order) return;
        try {
            await api.delete(`/orders/${order.id}/items/${itemId}`);
            toast.success("Ürün kaldırıldı");
            loadAll();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const existingTotal = order
        ? order.items.reduce((s, i) => s + i.price * i.qty, 0)
        : 0;
    const grandTotal = existingTotal + cartTotal;

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <AppHeader
                title={tableLabel(tableNum)}
                right={
                    <button
                        onClick={() => navigate(backPath)}
                        data-testid="back-to-tables"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#2C302E] bg-[#F3F1EB] hover:bg-[#E5E3D8] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {backLabel}
                    </button>
                }
            />

            <main className="max-w-[1600px] mx-auto px-6 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8">
                {/* Menu */}
                <section>
                    <div className="mb-6">
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">
                            Menü · {tableLabel(tableNum)}
                        </div>
                        <h2 className="font-display text-3xl font-semibold text-[#2C302E]">
                            {order ? "Bu masaya yeni ürün ekle" : "Sipariş aç"}
                        </h2>
                        <p className="text-sm text-[#59605B] mt-2">
                            {order
                                ? "İstediğin zaman yeni ürün ekleyebilirsin — mutfağa anında düşer."
                                : "Yemekleri seç, 'Mutfağa Gönder' ile siparişi başlat."}
                        </p>
                    </div>

                    {/* Categories */}
                    <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-2 px-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCategory(c)}
                                data-testid={`category-${c}`}
                                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                    activeCategory === c
                                        ? "bg-[#2C302E] text-white"
                                        : "bg-white text-[#2C302E] border border-[#E5E3D8] hover:border-[#2C302E]/40"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {filteredMenu.map((item) => {
                            const inCart = cart[item.id];
                            return (
                                <div
                                    key={item.id}
                                    data-testid={`menu-item-${item.id}`}
                                    className={`bg-white border rounded-xl p-5 transition-all ${
                                        inCart
                                            ? "border-[#D96C4A] shadow-md"
                                            : "border-[#E5E3D8] hover:-translate-y-0.5 hover:shadow-md hover:border-[#D96C4A]/30"
                                    }`}
                                >
                                    <div className="font-display text-base font-semibold text-[#2C302E] leading-tight mb-1">
                                        {item.name}
                                    </div>
                                    <div className="text-sm text-[#59605B] mb-3">{item.category}</div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-display text-lg font-semibold text-[#D96C4A]">
                                            ₺{item.price.toFixed(2)}
                                        </div>
                                        {inCart ? (
                                            <div className="flex items-center gap-1 bg-[#F3F1EB] rounded-lg p-1">
                                                <button
                                                    onClick={() => updateQty(item.id, -1)}
                                                    data-testid={`menu-dec-${item.id}`}
                                                    className="w-9 h-9 flex items-center justify-center rounded-md bg-white text-[#2C302E] hover:bg-[#E5E3D8] transition-colors"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="99"
                                                    value={inCart.qty}
                                                    onChange={(e) => setQty(item.id, e.target.value)}
                                                    onBlur={() => normalizeQty(item.id)}
                                                    onFocus={(e) => e.target.select()}
                                                    data-testid={`menu-qty-${item.id}`}
                                                    className="w-12 h-9 text-center text-base font-semibold text-[#2C302E] bg-white rounded-md border border-transparent focus:border-[#D96C4A] focus:outline-none"
                                                />
                                                <button
                                                    onClick={() => addToCart(item)}
                                                    data-testid={`menu-inc-${item.id}`}
                                                    className="w-9 h-9 flex items-center justify-center rounded-md bg-[#D96C4A] text-white hover:bg-[#C05A3B] transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => addToCart(item)}
                                                data-testid={`menu-add-${item.id}`}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2C302E] text-white text-sm font-medium hover:bg-[#1f221f] transition-colors"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Ekle
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredMenu.length === 0 && (
                            <div className="col-span-full p-8 text-center text-[#59605B] bg-white border border-dashed border-[#E5E3D8] rounded-xl">
                                Bu kategoride yemek yok.
                            </div>
                        )}
                    </div>
                </section>

                {/* Order Ticket */}
                <aside className="lg:sticky lg:top-24 h-fit">
                    <div className="bg-white border border-[#E5E3D8] rounded-2xl shadow-[2px_4px_12px_rgba(44,48,46,0.04)] overflow-hidden">
                        <div className="bg-[#F3F1EB] px-5 py-4 border-b border-[#E5E3D8]">
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B]">Sipariş Fişi</div>
                            <div className="font-display text-xl font-semibold text-[#2C302E]">{tableLabel(tableNum)}</div>
                        </div>

                        {/* Existing order items */}
                        {order && order.items.length > 0 && (
                            <div className="px-5 py-4 border-b border-[#E5E3D8] bg-[#FAF9F6]">
                                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] mb-3">
                                    Mutfakta · {order.items.length} ürün
                                </div>
                                <div className="space-y-2">
                                    {order.items.map((it) => {
                                        const sent = it.sent_qty || 0;
                                        const allSent = sent >= it.qty;
                                        const partial = sent > 0 && !allSent;
                                        return (
                                        <div
                                            key={it.id}
                                            data-testid={`existing-item-${it.id}`}
                                            className="flex items-center justify-between gap-2 text-sm"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    {allSent && (
                                                        <CheckCircle2 className="w-4 h-4 text-[#6B8E7B] shrink-0" />
                                                    )}
                                                    <span className={`truncate ${allSent ? "line-through text-[#59605B]" : "text-[#2C302E]"}`}>
                                                        {it.qty}× {it.name}
                                                    </span>
                                                </div>
                                                <div className={`text-[11px] mt-0.5 font-semibold uppercase tracking-wider ${
                                                    allSent ? "text-[#6B8E7B]"
                                                    : partial ? "text-[#E3B505]"
                                                    : "text-[#D96C4A]"
                                                }`}>
                                                    {allSent ? `✓ ${it.qty}/${it.qty} masaya gitti`
                                                    : partial ? `${sent}/${it.qty} gitti · ${it.qty - sent} mutfakta`
                                                    : `⏳ ${it.qty}/${it.qty} mutfakta hazırlanıyor`}
                                                </div>
                                                {it.notes && (
                                                    <div className="text-xs text-[#59605B] ml-6">Not: {it.notes}</div>
                                                )}
                                                {it.added_by === "mutfak" && (
                                                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#D96C4A] mt-0.5 text-right">
                                                        Mutfaktan eklendi
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#59605B] text-xs">
                                                    ₺{(it.price * it.qty).toFixed(2)}
                                                </span>
                                                {!allSent && (
                                                    <button
                                                        onClick={() => removeExistingItem(it.id)}
                                                        data-testid={`remove-existing-${it.id}`}
                                                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#59605B] hover:bg-red-50 hover:text-red-600 transition-colors"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* New cart */}
                        <div className="px-5 py-4 max-h-[420px] overflow-y-auto">
                            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] mb-3">
                                Yeni Ekle · {cartItems.length} ürün
                            </div>
                            {cartItems.length === 0 ? (
                                <div className="py-8 text-center text-sm text-[#59605B]">
                                    Soldan menüye dokunarak ürün ekleyin
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {cartItems.map(({ item, qty, notes }) => (
                                        <div key={item.id} data-testid={`cart-item-${item.id}`} className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-sm text-[#2C302E] truncate">
                                                        {item.name}
                                                    </div>
                                                    <div className="text-xs text-[#59605B]">
                                                        ₺{item.price.toFixed(2)}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 bg-[#F3F1EB] rounded-lg p-1">
                                                    <button
                                                        onClick={() => updateQty(item.id, -1)}
                                                        data-testid={`cart-dec-${item.id}`}
                                                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white text-[#2C302E] hover:bg-[#E5E3D8] transition-colors"
                                                    >
                                                        <Minus className="w-3.5 h-3.5" />
                                                    </button>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="99"
                                                        value={qty}
                                                        onChange={(e) => setQty(item.id, e.target.value)}
                                                        onBlur={() => normalizeQty(item.id)}
                                                        onFocus={(e) => e.target.select()}
                                                        data-testid={`cart-qty-${item.id}`}
                                                        className="w-10 h-7 text-center text-sm font-medium bg-white rounded-md border border-transparent focus:border-[#D96C4A] focus:outline-none"
                                                    />
                                                    <button
                                                        onClick={() => updateQty(item.id, 1)}
                                                        data-testid={`cart-inc-${item.id}`}
                                                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white text-[#2C302E] hover:bg-[#E5E3D8] transition-colors"
                                                    >
                                                        <Plus className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.id)}
                                                    data-testid={`cart-remove-${item.id}`}
                                                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#59605B] hover:bg-red-50 hover:text-red-600 transition-colors"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <input
                                                value={notes}
                                                onChange={(e) => updateNotes(item.id, e.target.value)}
                                                placeholder="Not (örn: az pişmiş)"
                                                data-testid={`cart-notes-${item.id}`}
                                                className="w-full text-xs bg-[#FAF9F6] border border-[#E5E3D8] rounded-md px-3 py-2 placeholder-[#59605B]/60 focus:outline-none focus:border-[#D96C4A]"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="px-5 py-4 border-t border-[#E5E3D8] bg-white">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-[#59605B]">Toplam</span>
                                <span className="font-display text-2xl font-semibold text-[#2C302E]" data-testid="cart-grand-total">
                                    ₺{grandTotal.toFixed(2)}
                                </span>
                            </div>
                            <button
                                onClick={sendToKitchen}
                                disabled={cartItems.length === 0 || sending}
                                data-testid="send-to-kitchen"
                                className="w-full bg-[#D96C4A] text-white font-medium px-6 py-4 rounded-xl hover:bg-[#C05A3B] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                {sending ? "Gönderiliyor..." : "Mutfağa Gönder"}
                            </button>
                        </div>
                    </div>
                </aside>
            </main>
        </div>
    );
}
